import dayjs from "dayjs";
import type { TimeRange } from "@volca/ui/components/ui/time-range-picker";
import { downloadCSV } from "@/core/utils/download-csv";
import { getUserFullName } from "@/core/utils/name";
import type { AccessPerson } from "@/payroll/workbook-access/types";
import { USER_ROLES } from "@/user/constants";
import { ROLE_TO_LABEL } from "@/user/constants";
import type {
  DetailedUser,
  UserStatsResponseItem,
  UserRole,
} from "@/user/user";
import type { AccountTableUser } from "./types";

export const getAccountTableUsers = (
  users: DetailedUser[],
  people: AccessPerson[] = [],
): AccountTableUser[] => {
  const accounts = new Map(users.map((user) => [user.id, user]));
  const accessPeople = new Map(people.map((person) => [person.id, person]));
  return [
    ...users.map((account) => ({
      id: account.id,
      name:
        getUserFullName(account) === "-"
          ? account.email
          : getUserFullName(account),
      email: account.email,
      role: account.role,
      referral_code: account.referral_code,
      account,
      accessPerson: accessPeople.get(account.id),
    })),
    ...people
      .filter(
        (person) =>
          !accounts.has(person.id) &&
          person.role.toUpperCase() !== USER_ROLES.TECH &&
          person.role.toUpperCase() !== USER_ROLES.ADMIN,
      )
      .map((person) => ({
        id: person.id,
        name: person.name,
        email: person.email,
        role: person.role.toUpperCase() as UserRole,
        accessPerson: person,
      })),
  ];
};

export const getUsersReportData = (
  users: AccountTableUser[],
  stats?: Record<string, UserStatsResponseItem>,
): AccountTableUser[] =>
  users.map((user) => ({ ...user, stats: stats?.[user.id]?.stats }));

export const downloadUsersReport = (
  users: AccountTableUser[],
  timeRange: TimeRange | null,
) => {
  const rows = users.map((user) => [
    user.name,
    user.email,
    ROLE_TO_LABEL[user.role] || "",
    user.referral_code ? `Referral${user.referral_code}` : "",
    (user.stats?.signup_count || 0).toString(),
    (user.stats?.total_referral_count || 0).toString(),
    (user.stats?.closed_referral_count || 0).toString(),
  ]);
  const filename = timeRange
    ? `users-report-${dayjs(timeRange.from).format("YYYY-MM-DD")}-to-${dayjs(timeRange.to).format("YYYY-MM-DD")}.csv`
    : "users-report.csv";
  downloadCSV(
    [
      [
        "Name",
        "Email",
        "Role",
        "Referral Code",
        "Signups",
        "Total Referrals",
        "Closed Referrals",
      ],
      ...rows,
    ],
    filename,
  );
};
